import React, { useState } from "react";
import "./styles/EducationCard.css";
import { verificationAPI } from "../../../services/verificationAPI";

const EducationCard = ({ education, onEdit, onDelete, isSelected }) => {
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(
    education.verification?.status
  );
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  // local modal state for resend confirmation
  const [resendOpen, setResendOpen] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    });
  };

  const getDateRange = () => {
    const startDate = formatDate(education.startDate);
    if (education.isOngoing) {
      return `${startDate} - Present`;
    }
    const endDate = formatDate(education.endDate);
    return `${startDate} - ${endDate}`;
  };

  const getVerificationStatus = () => {
    const status =
      verificationStatus || education.verificationStatus || "NOT_REQUESTED";

    const statusConfig = {
      PENDING: {
        label: "Verification Pending",
        class: "pending",
        icon: "⏳",
      },
      IN_PROGRESS: {
        label: "In Progress",
        class: "in-progress",
        icon: "🔄",
      },
      VALIDATED: { label: "Verified", class: "verified", icon: "✅" },
      REJECTED: {
        label: "Verification Rejected",
        class: "rejected",
        icon: "❌",
      },
      NOT_REQUESTED: {
        label: "Not Requested",
        class: "not-requested",
        icon: "⚪",
      },
    };

    return statusConfig[status] || statusConfig["NOT_REQUESTED"];
  };

  const verificationStatusData = getVerificationStatus();

  // open resend confirm modal
  const handleRequestVerificationClick = () => {
    if (!education.verifierEmail) {
      alert(
        "Please add a verifier email address before requesting verification."
      );
      return;
    }
    setResendOpen(true);
  };

  const closeResendConfirm = () => setResendOpen(false);

  const confirmResend = async () => {
    setResendOpen(false);
    setVerificationLoading(true);
    setError("");
    setSuccess("");

    try {
      await verificationAPI.requestEducationVerification(education.id);
      setVerificationStatus("PENDING");

      const message =
        verificationStatusData.class === "not-requested"
          ? "Verification request sent successfully!"
          : "Verification request resent successfully!";

      setSuccess(message);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to request verification"
      );
    } finally {
      setVerificationLoading(false);
    }
  };

  return (
    <div className={`education-card ${isSelected ? "selected" : ""}`}>
      {/* TOP ROW: info left, icons right */}
      <div className="education-row">
        <div className="education-main">
          <h3 className="education-degree">{education.degree}</h3>
          <div className="education-institution">
            🎓 {education.institution}
          </div>

          {education.fieldOfStudy && (
            <div className="education-details">
              <strong>Field of Study:</strong>
              <p className="education-field">{education.fieldOfStudy}</p>
            </div>
          )}

          <div className="verification-status">
            <span
              className={`verification-badge ${verificationStatusData.class}`}
            >
              <span className="verification-icon">
                {verificationStatusData.icon}
              </span>
              <span className="verification-label">
                {verificationStatusData.label}
              </span>
            </span>
          </div>

          {education.verifierEmail && (
            <div className="verifier-info">
              <strong>Verifier:</strong>
              <div className="verifier-email">{education.verifierEmail}</div>
            </div>
          )}
        </div>

        <div className="education-actions">
          <button
  onClick={onEdit}
  className="btn-icon btn-edit"
  title="Edit Education"
>
  <svg xmlns="www.w3.org" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
</button>

<button
  onClick={onDelete}
  className="btn-icon btn-delete"
  title="Delete Education"
>
  <svg xmlns="www.w3.org" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="red" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
</button>

        </div>
      </div>

      {/* BOTTOM ROW: period left, resend button right */}
      <div className="education-footer">
        <div className="education-dates">{getDateRange()}</div>

        {(verificationStatusData.class === "not-requested" ||
          verificationStatusData.class === "pending" ||
          verificationStatusData.class === "rejected") && (
          <button
            onClick={handleRequestVerificationClick}
            disabled={verificationLoading}
            className="resend-btn"
            title={
              verificationStatusData.class === "not-requested"
                ? "Request Education Verification"
                : "Resend Verification Request"
            }
          >
            {verificationLoading
              ? "Processing..."
              : verificationStatusData.class === "not-requested"
              ? "Request Verification"
              : "Resend Request"}
          </button>
        )}
      </div>

      {success && <div className="message success-message">✅ {success}</div>}
      {error && <div className="message error-message">❌ {error}</div>}

      {/* Resend confirmation modal (same style as delete) */}
      {resendOpen && (
        <div className="confirm-overlay">
          <div className="confirm-dialog">
            <h3 className="confirm-title">Are you sure?</h3>
            <p className="confirm-body">
              This will send a verification request to {education.verifierEmail}.
            </p>
            <div className="confirm-actions">
              <button
  className="confirm-btn confirm-cancel"
  onClick={closeResendConfirm}
>
  Cancel
</button>
<button className="confirm-btn confirm-send" onClick={confirmResend}>
  Send
</button>

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EducationCard;
